// Platform Admin — 🏆 Outreach Wins Dashboard
// =============================================
// Closed-loop performance metrics for past Auto-Launch Outreach campaigns:
// summary KPIs, state leaderboard, per-launch table with delivery & read rates.
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  partial: 'bg-orange-100 text-orange-800 border-orange-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
  failed: 'bg-rose-100 text-rose-800 border-rose-200',
};

const STATUS_LABEL_HI = {
  completed: 'पूर्ण',
  in_progress: 'चालू',
  partial: 'आंशिक',
  cancelled: 'रद्द',
  failed: 'विफल',
};

export default function OutreachWinsPanel({ token, language = 'hi' }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async (r = range) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/school-finder/geo-map/outreach/wins?range=${r}`, { headers });
      const j = await res.json();
      if (j.success) setData(j);
    } catch (e) { console.warn('wins load:', e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading && !data) return <div className="p-6 text-sm text-slate-500">Loading wins…</div>;
  if (!data) return <div className="p-6 text-sm text-rose-600">Failed to load wins</div>;

  const { summary, state_leaderboard: leaderboard, launches } = data;

  return (
    <div className="space-y-4" data-testid="outreach-wins-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-rose-600 via-fuchsia-600 to-purple-700 text-white rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-black flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              🏆 {language === 'hi' ? 'Outreach Wins Dashboard' : 'Outreach Wins Dashboard'}
            </h2>
            <p className="text-sm text-white/90 mt-1">
              {language === 'hi'
                ? 'हर state-level Auto-Launch की performance — closed-loop ROI tracking।'
                : 'Performance of every state-level Auto-Launch — closed-loop ROI tracking.'}
            </p>
          </div>
          <div className="inline-flex bg-white/15 rounded-lg p-0.5 gap-0.5" data-testid="wins-range-tabs">
            {['7d', '30d', '90d', 'all'].map((r) => (
              <button
                key={r}
                onClick={() => { setRange(r); load(r); }}
                className={`px-2.5 py-1 text-xs font-bold rounded transition ${range === r ? 'bg-white text-rose-700' : 'text-white/90 hover:bg-white/10'}`}
                data-testid={`wins-range-${r}`}
              >{r.toUpperCase()}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="wins-summary">
        <KpiTile label={language === 'hi' ? 'Total Launches' : 'Total Launches'} value={summary.total_launches} color="rose" />
        <KpiTile label={language === 'hi' ? 'कुल Recipients' : 'Total Recipients'} value={summary.total_recipients} color="purple" />
        <KpiTile label={language === 'hi' ? 'Sent' : 'Sent'} value={summary.total_sent} color="blue" />
        <KpiTile label={language === 'hi' ? 'Delivered' : 'Delivered'} value={summary.total_delivered} color="cyan" suffix={summary.delivery_rate ? ` (${summary.delivery_rate}%)` : ''} />
        <KpiTile label={language === 'hi' ? 'Read' : 'Read'} value={summary.total_read} color="emerald" suffix={summary.read_rate ? ` (${summary.read_rate}%)` : ''} />
        <KpiTile label={language === 'hi' ? 'Failed' : 'Failed'} value={summary.total_failed} color="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* State Leaderboard */}
        <div className="bg-white rounded-2xl border p-4 lg:col-span-1" data-testid="state-leaderboard">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-1" style={{ fontFamily: 'Outfit' }}>
            🥇 {language === 'hi' ? 'Top Performing States' : 'Top Performing States'}
          </h3>
          {leaderboard.length === 0 ? (
            <p className="text-xs text-slate-500">{language === 'hi' ? 'अभी कोई data नहीं' : 'No launches yet'}</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((s, i) => (
                <div key={s.state} className="text-xs" data-testid={`leaderboard-row-${i}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-slate-900">
                      <span className="inline-block w-5 text-slate-400">{i + 1}.</span> {s.state}
                    </span>
                    <span className="text-emerald-700 font-bold">{s.read_rate}%</span>
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-500 ml-5 mb-1">
                    <span>{s.launches} launches · {s.sent} sent · {s.read} read</span>
                  </div>
                  <div className="bg-slate-100 rounded h-1.5 ml-5 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-rose-500 h-1.5" style={{ width: `${Math.max(s.read_rate, 2)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Launches Table */}
        <div className="bg-white rounded-2xl border lg:col-span-2 overflow-hidden" data-testid="launches-table">
          <div className="px-4 py-3 border-b">
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Outfit' }}>
              📋 {language === 'hi' ? 'Recent Launches' : 'Recent Launches'} ({launches.length})
            </h3>
          </div>
          {launches.length === 0 ? (
            <p className="text-xs text-slate-500 p-4">{language === 'hi' ? 'अभी कोई launch नहीं हुआ — India Map से Auto-Launch trigger करें।' : 'No launches yet — trigger one from India Map.'}</p>
          ) : (
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase text-slate-600 tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">State</th>
                    <th className="px-2 py-2 text-left">Launched</th>
                    <th className="px-2 py-2 text-left">Mode</th>
                    <th className="px-2 py-2 text-right">Recipients</th>
                    <th className="px-2 py-2 text-right">Sent</th>
                    <th className="px-2 py-2 text-right">Delivered</th>
                    <th className="px-2 py-2 text-right">Read</th>
                    <th className="px-2 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {launches.map((l, i) => (
                    <tr key={i} className="hover:bg-slate-50" data-testid={`launch-row-${i}`}>
                      <td className="px-3 py-2 font-bold text-slate-900">{l.state}</td>
                      <td className="px-2 py-2 text-[10px] text-slate-600">{new Date(l.started_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="px-2 py-2 text-[10px]">
                        <span className="px-1.5 py-0.5 bg-slate-100 rounded">{l.schedule_mode}</span>
                      </td>
                      <td className="px-2 py-2 text-right">{l.recipients_planned.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-right">{l.sent.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-right">
                        {l.delivered.toLocaleString('en-IN')}
                        {l.delivery_rate > 0 && <span className="text-cyan-700 ml-1">({l.delivery_rate}%)</span>}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {l.read.toLocaleString('en-IN')}
                        {l.read_rate > 0 && <span className="text-emerald-700 ml-1">({l.read_rate}%)</span>}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[l.launch_status] || STATUS_BADGE.cancelled}`}>
                          {language === 'hi' ? STATUS_LABEL_HI[l.launch_status] || l.launch_status : l.launch_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Hint */}
      {summary.total_launches > 0 && summary.read_rate < 5 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="wins-low-read-hint">
          <p className="text-xs text-amber-900">
            ⚡ {language === 'hi'
              ? `औसत read rate ${summary.read_rate}% — Best Hour mode use करें या caption A/B test करें।`
              : `Average read rate is ${summary.read_rate}% — try Best Hour mode or A/B test captions.`}
          </p>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, color = 'rose', suffix = '' }) {
  const colorMap = {
    rose: 'from-rose-500 to-pink-600',
    purple: 'from-purple-500 to-fuchsia-600',
    blue: 'from-blue-500 to-indigo-600',
    cyan: 'from-cyan-500 to-blue-600',
    emerald: 'from-emerald-500 to-teal-600',
    slate: 'from-slate-500 to-slate-700',
  };
  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.rose} text-white rounded-xl p-3`}>
      <p className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">{label}</p>
      <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>
        {(value || 0).toLocaleString('en-IN')}<span className="text-xs">{suffix}</span>
      </p>
    </div>
  );
}
